import { Buyer } from '@40seas/entities/buyers';
import { EnumSchema, Executor, FromSchema, ZodDate } from '@40seas/entities/common';
import { Seller } from '@40seas/entities/sellers';
import {
    Address,
    AddressSchema,
    BaseEntity,
    Currency,
    Discount,
    DiscountSchema,
    LineItem,
    LineItemSchema,
    PaymentOptionWithFee,
    PaymentOptionWithFeeSchema,
    PaymentTermsOptions,
    PaymentTermsOptionSchema,
    PaymentTermType,
    PercentageValue,
    PercentageValueSchema,
    WithBaseEntitySchema,
} from '@40seas/models';
import { getRoundedDecimal } from '@40seas/shared/calculations';
import { z } from 'zod';
import { InvoiceConfirmations, InvoiceConfirmationsScheme } from './invoice.confirmations';
import { InvoiceFinancing, InvoiceFinancingSchema } from './invoice.financing';
import { InvoiceMode } from './invoice.mode.enum';
import { InvoiceResult, InvoiceResultSchema } from './invoice.result';
import { InvoiceStatus } from './invoice.status.enum';
import { InvoiceTransitType } from './invoice.transit.type.enum';

export const InvoiceSchema = WithBaseEntitySchema(
    z.object({
        amount: z
            .number()
            .gt(0)
            .max(10000000)
            .transform((amount) => getRoundedDecimal(amount))
            .openapi({ effectType: 'input' }),
        externalId: z
            .union([z.string(), z.number()])
            .optional()
            .transform((val) => (val === undefined ? undefined : String(val)))
            .openapi({ effectType: 'input' }),
        buyer: z
            .string()
            .regex(/^[0-9a-f]{24}$/i)
            .or(Buyer['schema']),
        order: z.string().optional(),
        lineItems: z.array(LineItemSchema).optional(),
        shippingDate: ZodDate.nullish(),
        arrivalDate: ZodDate.nullish(),
        shippingAddress: AddressSchema.optional(),
        billingAddress: AddressSchema.optional(),
        dueDate: ZodDate.optional(),
        issueDate: ZodDate.optional(),
        paymentTerms: z.array(EnumSchema(PaymentTermType)).optional(),
        paymentOptionWithFee: PaymentOptionWithFeeSchema.nullish(),
        paymentOptions: z.array(PaymentOptionWithFeeSchema).optional(),
        status: EnumSchema(InvoiceStatus),
        total: z.number().nullish(),
        totalFee: z.number().nullish(),
        totalDiscount: z.number().optional(),
        currency: EnumSchema(Currency)
            .transform((currency) => (currency === Currency.CNH ? Currency.CNY : currency))
            .openapi({
                effectType: 'input',
                description: 'Note: CNH input will be transformed to CNY as we receive in request CNH but support CNY',
            }),
        mode: EnumSchema(InvoiceMode).optional(),
        discount: PercentageValueSchema.nullish(),
        memo: z.string().nullish(),
        amountPaid: z.number().nullish(),
        shippingFee: z.number().nullish(),
        tax: PercentageValueSchema.nullish(),
        confirmations: InvoiceConfirmationsScheme.optional(),
        transitType: EnumSchema(InvoiceTransitType).optional(),
        discounts: z.array(DiscountSchema).optional(),
        balance: z.number().optional(),
        result: InvoiceResultSchema.nullish(),
        checkout: z.any().optional(),
        providerId: z
            .union([z.string(), z.number()])
            .optional()
            .transform((val) => (val === undefined ? undefined : String(val)))
            .openapi({ effectType: 'input' }),
        paymentTermsOptions: z
            .record(EnumSchema(PaymentTermType), z.array(PaymentTermsOptionSchema.partial()))
            .optional()
            .openapi({ effectType: 'input' }),
        financing: InvoiceFinancingSchema.optional(),
    }),
).strict();

export class Invoice extends FromSchema(InvoiceSchema) implements BaseEntity {
    id: string;
    buyer: string | Buyer;
    order?: string;
    lineItems?: LineItem[];
    shippingDate?: Date; // TBD - remove
    arrivalDate?: Date; // TBD - remove
    shippingAddress?: Address;
    billingAddress?: Address;
    result?: InvoiceResult;
    dueDate?: Date;
    issueDate?: Date;
    paymentTerms?: PaymentTermType[];
    paymentTermsOptions?: PaymentTermsOptions;
    paymentOptionWithFee?: PaymentOptionWithFee; // TBD - remove
    paymentOptions?: PaymentOptionWithFee[];
    externalId?: string;
    providerId?: string;
    createdAt: Date;
    updatedAt: Date;
    createdBy: Executor;
    status: InvoiceStatus;
    mode?: InvoiceMode;
    amount: number;
    total?: number; // TBD - remove
    totalFee?: number; // TBD - remove
    totalDiscount?: number; // TBD - remove
    currency: Currency;
    discount?: PercentageValue;
    memo?: string;
    amountPaid?: number;
    shippingFee?: number;
    tax?: PercentageValue;
    seller: string | Seller;
    tenantId: string;
    metadata?: { [key: string]: string };
    timeline?: { [status: string]: { updatedAt: Date } };
    confirmations?: InvoiceConfirmations;
    transitType?: InvoiceTransitType;
    discounts?: Discount[];
    balance?: number;
    financing?: InvoiceFinancing;
}

export class MultiTenantInvoice extends Invoice {}

export const NOT_ACTIVE_STATUSES = [InvoiceStatus.Canceled, InvoiceStatus.Expired, InvoiceStatus.Charged, InvoiceStatus.Defaulted];
